import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Dropdown, MenuItem } from 'react-bootstrap'
import styled from 'styled-components'

import i18n from 'live/services/i18n'
import globalConfirmation from 'live/services/global-confirmation'

import { GalleryRow } from './gallery/GalleryRow'
import wrapperDecorator from '../wrapperDecorator'
import { WidgetPageGalleryWidget } from '../types'
import { getNextId, getNextIdWidgetGallery } from '../getNextId'
import RGL, { WidthProvider } from 'react-grid-layout'
import { getWidget } from '../../../../api/getDashboards'
import { getTypes, getWrapper } from '../wrapperDecorator'
import { WidgetDescription } from './gallery/WidgetDescription'
import { Edit } from '../../../../common/components/actions/edit'
import { WidgetTypes } from '../../../../common/types/api/report'
import { Galleries, WidgetGallery } from '../../../../common/types/api/page'
import {
    getGalleryRowLayout,
    getDefaultWidget,
    getDefaultBlockHTML,
    getDefaultBlockPipes,
    GALLERY_CONTENT_MIN_WIDTH,
    GALLERY_CONTENT_MIN_HEIGHT,
    getDefaultContent
} from './gallery/mountGallery'
import { DashboardWidget } from '../../../../common/types/api/DashboardsResponse'
import { select } from 'd3'
import WidgetEditor from '../../ReportContent/components/GalleryEditor/galleryEditor'
import HtmlEditor from '../../../components/Content/emailType/htmlEditor'
import TextEditor from '../../../components/Content/emailType/textEditor'

import { getGalleryLayout, HEIGHT_ROW, MAX_WIDTH, magicNumber } from './getGalleryLayout'
import { CustomToolTip } from '../../../../common/components/CustomToolTip/CustomToolTip'
import GalleryMovementDropdownIcon from './GalleryMovementDropdownIcon'

const GridLayout = WidthProvider(RGL)
interface Props extends Galleries {
    openEditor: (sizes: {
        indexColumn: number
        indexRow: number
        widthMax?: number
        subContentType: WidgetTypes
    }) => void
    onChange: (t: WidgetPageGalleryWidget) => void
    editMode: boolean
}

export interface PositionMovimentComponent {
    top: number
    left: number
    zIndex: number
}

const getContentEditor = (subContentTye: WidgetTypes): any => {
    switch (subContentTye) {
        case WidgetTypes.GALLERY:
            return WidgetEditor
        case WidgetTypes.PIPES:
            return HtmlEditor
        case WidgetTypes.HTML:
            return TextEditor
        default:
            return undefined
    }
}

const useClickOutside = (ref, open: boolean, callback) => {
    const handleClick = e => {
        if (ref.current && !ref.current.contains(e.target)) {
            callback()
        }
    }
    React.useEffect(() => {
        if (open) {
            window.addEventListener('click', handleClick)            
            return () => {
                window.removeEventListener('click', handleClick)
            }
        } else {
            window.removeEventListener('click', handleClick)
        }
    }, [open])
}

export function ComponentWidgetGallery({
    columns,
    id,
    openEditor,
    onChange,
    fileName,
    style,
    editMode,
    replication
}: Props): JSX.Element {
    if (!columns) {
        onChange({
            type: WidgetTypes.GALLERY,
            config: {
                replication,
                columns: [],
                id,
                fileName,
                style
            }
        })
        return <div />
    }

    const [{ openMenu, positionMovimentComponent, focus }, setState] = useState<{
        openMenu: boolean
        positionMovimentComponent: PositionMovimentComponent
        focus: {
            columns: any[][]
            row: number
            col: number
        }
    }>({
        openMenu: false,
        focus: {
            col: -1,
            row: -1,
            columns: []
        },
        positionMovimentComponent: {
            left: 0,
            top: 0,
            zIndex: -1
        }
    })

    const setOpenMenu = (isOpen: boolean) => setState(currentState => ({ ...currentState, openMenu: isOpen }))

    const el = useRef()
    const dropdownElement = useRef()

    useClickOutside(dropdownElement, positionMovimentComponent.zIndex !== -1, () => {
        if (positionMovimentComponent.zIndex !== -1) {
            setState(currentState => ({
                ...currentState,
                focus: {
                    col: -1,
                    row: -1,
                    columns: []
                },
                positionMovimentComponent: {
                    left: 0,
                    top: 0,
                    zIndex: -1
                }
            }))
        }
    })

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap',
                position: 'relative'
            }}
            ref={el}
        >
            <div style={{ flex: 1 }}>
                {columns.map((collumn, indexRow) => {
                    return (
                        <GalleryRow
                            key={`gallery-collum-${indexRow}`}
                            column={collumn}
                            columns={columns}
                            editMode={editMode}
                            indexRow={indexRow}
                            onChange={currentColumns => {
                                return onChange({
                                    type: WidgetTypes.GALLERY,
                                    config: {
                                        replication,
                                        columns: currentColumns,
                                        id,
                                        fileName,
                                        style
                                    }
                                })
                            }}
                            openEditor={openEditor}
                            movementIconFocus={focus}
                            openMovimentModal={(currentElement: Element, row: number, col: number) => {
                                const currentElementRect = currentElement.getBoundingClientRect()
                                const element = el.current as Element
                                if (element) {
                                    const elementRect = element.getBoundingClientRect()
                                    setTimeout(() => {
                                        setState(currentState => ({
                                            ...currentState,
                                            focus: {
                                                columns,
                                                col,
                                                row
                                            },
                                            positionMovimentComponent: {
                                                ...currentState.positionMovimentComponent,
                                                left: currentElementRect.x - elementRect.x + 50,
                                                top: currentElementRect.y - elementRect.y + currentElementRect.height,
                                                zIndex: 9999
                                            }
                                        }))
                                    }, 200)
                                }
                            }}
                            onChangeRow={currentColumns => {
                                const newColumns = [...columns]
                                newColumns[indexRow] = currentColumns
                                return onChange({
                                    type: WidgetTypes.GALLERY,
                                    config: {
                                        replication,
                                        columns: newColumns,
                                        id,
                                        fileName,
                                        style
                                    }
                                })
                            }}
                        />
                    )
                })}
            </div>
            <ContentButtonDiv>
                <ContentDropDown open={editMode}>
                    <ButtonAddContent className="btn" onClick={() => setOpenMenu(!openMenu)} open={openMenu}>
                        <div className="buttonStyle">
                            <i className="far fa-plus" />
                            {i18n('Add Content')}
                            <i className="fas fa-caret-down" />
                        </div>
                    </ButtonAddContent>
                    <ContentDropDownButton
                        id={`--btn-add-row-${id}`}
                        pullRight
                        open={openMenu}
                        onToggle={() => {
                            setOpenMenu(!openMenu)
                        }}
                    >
                        <Dropdown.Menu open={openMenu}>
                            {getTypes().map(type => {
                                const decodeLabelBtn = (type: WidgetTypes): JSX.Element => {
                                    switch (type) {
                                        case WidgetTypes.PIPES:
                                            return (
                                                <>
                                                    <i className="far fa-code" />
                                                    {i18n('HTML')}
                                                </>
                                            )
                                        case WidgetTypes.HTML:
                                            return (
                                                <>
                                                    <i className="far fa-text" />
                                                    {i18n('Text')}
                                                </>
                                            )

                                        case WidgetTypes.GALLERY:
                                            return (
                                                <>
                                                    <i className="far fa-file-import" />
                                                    {i18n('Import Widget')}
                                                </>
                                            )
                                    }
                                }

                                return (
                                    <ContentDropDownItem
                                        key={`config-type-${type}`}
                                        onClick={() => {
                                            const lastLineIdx = columns.length - 1
                                            let lastLineSpace = 0
                                            let lastLineHight = GALLERY_CONTENT_MIN_HEIGHT
                                            if (lastLineIdx >= 0) {
                                                lastLineSpace = columns[lastLineIdx].reduce(
                                                    (s, { width }) => s + width,
                                                    0
                                                )
                                                lastLineHight = columns[lastLineIdx].reduce(
                                                    (s, { height }) => Math.max(s, height),
                                                    GALLERY_CONTENT_MIN_HEIGHT
                                                )
                                            }
                                            const newColumns = [...columns]
                                            const newContentId = getNextIdWidgetGallery(columns)
                                            const newContent = getDefaultContent(
                                                type,
                                                newContentId,
                                                GALLERY_CONTENT_MIN_WIDTH,
                                                lastLineHight
                                            )
                                            if (
                                                lastLineSpace + GALLERY_CONTENT_MIN_WIDTH <= MAX_WIDTH &&
                                                lastLineIdx >= 0
                                            ) {
                                                newColumns[lastLineIdx] = [...newColumns[lastLineIdx], newContent]
                                            } else {
                                                newColumns.push([
                                                    {
                                                        ...newContent,
                                                        height: GALLERY_CONTENT_MIN_HEIGHT
                                                    }
                                                ])
                                            }

                                            onChange({
                                                type: WidgetTypes.GALLERY,
                                                config: {
                                                    replication,
                                                    columns: newColumns,
                                                    id,
                                                    fileName,
                                                    style
                                                }
                                            })
                                        }}
                                    >
                                        <div className="baseMenuItem">
                                            <span className="contentMenuItem">{decodeLabelBtn(type)}</span>
                                        </div>
                                    </ContentDropDownItem>
                                )
                            })}
                        </Dropdown.Menu>
                    </ContentDropDownButton>
                    {/* */}
                </ContentDropDown>
            </ContentButtonDiv>
            <div
                ref={dropdownElement}
                style={{
                    height: 'auto',
                    width: '220px',
                    backgroundColor: 'white',
                    position: 'absolute',
                    display: positionMovimentComponent.zIndex == -1 ? 'none' : undefined,
                    ...positionMovimentComponent
                }}
            >
                <GalleryMovementDropdownIcon
                    {...focus}
                    editMode={editMode}
                    id={`dropdown-gallery-gallery-${id}`}
                    onChangeColumns={currentColumn => {
                        onChange({
                            type: WidgetTypes.GALLERY,
                            config: {
                                replication,
                                columns: currentColumn,
                                id,
                                fileName,
                                style
                            }
                        })
                        setState(currentState => ({
                            ...currentState,
                            focus: {
                                col: -1,
                                row: -1,
                                columns: []
                            }
                        }))
                    }}
                />
            </div>
        </div>
    )
}

const ContentButtonDiv = styled.div`
    height: 48px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${({ theme }) => theme['gray-0']};
`

const ButtonAddContent = styled.button<{ open: boolean }>`
    width: 170px;
    font-family: ${({ theme }) => theme['font-semibold']} !important;
    color: ${({ theme, open }) => theme[`${open ? 'gray-0' : 'gray-800'}`]};
    background: ${({ theme, open }) => theme[`${open ? 'gray-300' : 'gray-0'}`]};
    box-shadow: none;
    border: 1px solid ${({ theme }) => theme['gray-400']};
    border-radius: 3px;

    .buttonStyle {
        display: flex;
        justify-content: space-around;
        align-items: center;
    }
`

const ContentDropDown = styled.div<{ open: boolean }>`
    display: ${({ open }) => (open ? 'block' : 'none')};
    .dropdown-toggle {
        display: flex !important;
        justify-content: space-around !important;
        align-items: center !important;
        background: transparent;
        min-width: 177px;
        height: 32px;
        font-family: ${({ theme }) => theme['font-semibold']} !important;
        font-weight: 600 !important;
        color: ${({ theme }) => theme['gray-800']};
        border: 1px solid ${({ theme }) => theme['gray-400']};
        border-radius: 3px;
        .caret {
            color: ${({ theme }) => theme['gray-800']};
        }
    }

    .dropdown-toggle:focus,
    .dropdown-toggle:hover {
        background: ${({ theme }) => theme['gray-300']};
        font-family: ${({ theme }) => theme['font-semibold']} !important;
        color: ${({ theme }) => theme['gray-800']};
        box-shadow: none;
        border: 1px solid ${({ theme }) => theme['gray-400']};
        border-radius: 3px;
    }

    .dropdown-menu {
        margin: 0;
        padding: 0;
        margin-top: 4px;
        min-width: 177px;
        border-radius: 4px;
        overflow: hidden;
    }

    .divider {
        height: 1px;
    }
`

const ContentDropDownButton = styled(Dropdown)`
    background: white !important;
    font-family: ${({ theme }) => theme['font-semibold']} !important;
    top: 16px;
`

const ContentDropDownItem = styled(MenuItem)`
    height: 38px;

    a {
        padding: 0 !important;
        height: 100%;

        .baseMenuItem {
            display: flex;
            align-items: center;
            width: 100%;
            height: 100%;

            .contentMenuItem {
                font-family: ${({ theme }) => theme['font-regular']} !important;
                font-weight: 400 !important;
                color: ${({ theme }) => theme['gray-800']};

                i {
                    color: ${({ theme }) => theme['gray-600']};
                    padding: 8px 8px 8px 16px;
                    /* padding-left: 8px; */
                }
            }

            :hover {
                .contentMenuItem {
                    font-family: ${({ theme }) => theme['font-semibold']} !important;
                    font-weight: 600 !important;
                    color: ${({ theme }) => theme['gray-800']};

                    i {
                        color: ${({ theme }) => theme['blue-500']};
                    }
                }
            }
        }
    }
`

export default wrapperDecorator({ type: WidgetTypes.GALLERY, minHeight: 50, Editor: getContentEditor })(
    ComponentWidgetGallery
)
